import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { motion } from 'motion/react';
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  Video,
  Mic,
  ClipboardCheck,
  IndianRupee,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const QUOTA_LABELS = {
  credits: 'Credits',
  quota_team_users: 'Team Users',
};

const RATE_LABELS = {
  ondemand_rate_interview: 'Interview',
  ondemand_rate_assessment: 'Assessment',
};

const INITIAL_FORM = {
  name: '',
  slug: '',
  displayName: '',
  description: '',
  currency: 'INR',
  monthlyPrice: '',
  yearlyDiscount: '',
  target: '',
  credits: '',
  quota_team_users: '',
  ondemand_rate_interview: '',
  ondemand_rate_assessment: '',
  isHidden: false,
  visibleToOrgIdsStr: '',
  basePlan: '',
};

const BASE_PLANS = [
  { value: '', label: 'None (custom)' },
  { value: 'starter', label: 'Starter base' },
  { value: 'core', label: 'Core base' },
];

function InternalPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...INITIAL_FORM });

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/internal/packages');
      setPackages(data.packages || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const handleCreate = () => {
    setEditingId(null);
    setForm({ ...INITIAL_FORM });
    setShowCreate(true);
  };

  const handleEdit = (pkg) => {
    setShowCreate(false);
    setForm({
      name: pkg.name || '',
      slug: pkg.slug || '',
      displayName: pkg.displayName || '',
      description: pkg.description || '',
      currency: pkg.currency || 'INR',
      monthlyPrice: pkg.monthlyPrice != null ? pkg.monthlyPrice : '',
      yearlyDiscount: pkg.yearlyDiscount != null ? pkg.yearlyDiscount : '',
      target: pkg.target || '',
      credits: pkg.credits != null ? pkg.credits : '',
      quota_team_users: pkg.quota_team_users != null ? pkg.quota_team_users : '',
      ondemand_rate_interview: pkg.ondemand_rate_interview != null ? pkg.ondemand_rate_interview : '',
      ondemand_rate_assessment: pkg.ondemand_rate_assessment != null ? pkg.ondemand_rate_assessment : '',
      isHidden: !!pkg.isHidden,
      visibleToOrgIdsStr: Array.isArray(pkg.visibleToOrgIds) ? pkg.visibleToOrgIds.map((id) => String(id)).join('\n') : '',
      basePlan: pkg.basePlan || '',
    });
    setEditingId(pkg._id);
  };

  const handleCloseForm = () => {
    setShowCreate(false);
    setEditingId(null);
    setForm({ ...INITIAL_FORM });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const visibleToOrgIds = (form.visibleToOrgIdsStr || '')
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        ...form,
        visibleToOrgIds,
        monthlyPrice: form.monthlyPrice === '' ? null : Number(form.monthlyPrice),
        yearlyDiscount: form.yearlyDiscount === '' ? null : Number(form.yearlyDiscount),
        credits: form.credits === '' ? null : Number(form.credits),
        quota_team_users: form.quota_team_users === '' ? null : Number(form.quota_team_users),
        ondemand_rate_interview: form.ondemand_rate_interview === '' ? null : Number(form.ondemand_rate_interview),
        ondemand_rate_assessment: form.ondemand_rate_assessment === '' ? null : Number(form.ondemand_rate_assessment),
      };

      if (editingId) {
        await axios.patch(`/api/internal/packages/${editingId}`, payload);
        toast.success('Package updated successfully');
      } else {
        await axios.post('/api/internal/packages', payload);
        toast.success('Package created successfully');
      }
      handleCloseForm();
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? Organisations using this package must be reassigned first.')) return;
    try {
      await axios.delete(`/api/internal/packages/${id}`);
      toast.success('Package deleted');
      handleCloseForm();
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete package');
    }
  };

  const inputCls =
    'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] focus:outline-none';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  const QuotaBadge = ({ icon: Icon, value, label }) =>
    value != null ? (
      <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF]/5 px-2.5 py-1 text-xs font-medium text-[#007BFF] border border-[#007BFF]/10">
        <Icon className="h-3.5 w-3.5" />
        <span>{value}</span>
        <span className="text-gray-500">{label}</span>
      </span>
    ) : null;

  return (
    <div className="min-h-full w-full bg-[#F7F9FC]" style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Package Creation
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage custom packages. Updates propagate to all linked organisations immediately.
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#007BFF] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0066CC] hover:shadow transition-all duration-200 shrink-0"
        >
          <Plus className="h-4 w-4" />
          Create package
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-[#007BFF]" />
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            {packages.length === 0 && !showCreate ? (
              <div className="rounded-2xl border border-gray-200/80 bg-white shadow-sm p-16 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#007BFF]/10 text-[#007BFF]">
                  <Package className="h-8 w-8" />
                </div>
                <p className="mt-4 text-lg font-medium text-gray-900">No packages yet</p>
                <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
                  Create a package to offer custom plans to organisations. Updates sync to all linked orgs instantly.
                </p>
                <button
                  type="button"
                  onClick={handleCreate}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#007BFF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0066CC] shadow-sm transition-all"
                >
                  <Plus className="h-4 w-4" />
                  Create package
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {packages.map((pkg) => (
                  <motion.div
                    key={pkg._id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={cn(
                      'rounded-2xl border border-l-0 bg-white shadow-sm overflow-hidden transition-all duration-200 relative',
                      editingId === pkg._id
                        ? 'border-[#007BFF] ring-2 ring-[#007BFF]/20 shadow-md'
                        : 'border-gray-200/80 hover:border-[#007BFF]/30 hover:shadow'
                    )}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5">
                      <div className="flex items-start gap-4 min-w-0">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#007BFF]/15 to-[#007BFF]/5 text-[#007BFF]">
                          <Package className="h-6 w-6" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900 text-base">
                              {pkg.displayName || pkg.name}
                            </span>
                            <span className="rounded-lg bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                              {pkg.slug}
                            </span>
                            {pkg.isHidden && (
                              <span className="rounded-lg bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                                Hidden
                              </span>
                            )}
                          </div>
                          <p className="mt-2 flex items-center gap-1 text-sm font-semibold text-gray-900">
                            {pkg.monthlyPrice != null ? (
                              <>
                                <IndianRupee className="h-3.5 w-3.5" />
                                {Number(pkg.monthlyPrice).toLocaleString()}/mo
                              </>
                            ) : (
                              <span className="text-gray-600">Custom pricing</span>
                            )}
                          </p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {pkg.credits != null ? (
                              <>
                                <QuotaBadge icon={Package} value={pkg.credits} label="Credits" />
                                <span className="text-[10px] text-gray-400 italic">1 int = 1cr, 1 ass = 0.5cr</span>
                              </>
                            ) : (
                              <span className="text-xs text-gray-500">Custom credits</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleEdit(pkg)}
                          title="Edit package"
                          className="rounded-lg p-2.5 text-gray-500 hover:bg-[#007BFF]/10 hover:text-[#007BFF] transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {(pkg.slug || '').toLowerCase() !== 'enterprise' && (
                          <button
                            type="button"
                            onClick={() => handleDelete(pkg._id)}
                            title="Delete package"
                            className="rounded-lg p-2.5 text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {editingId === pkg._id && (
                      <form onSubmit={handleSubmit} className="border-t border-gray-100 p-6 bg-[#F7F9FC]/60">
                        <PackageFormFields form={form} setForm={setForm} labelCls={labelCls} inputCls={inputCls} />
                        <div className="flex items-center justify-end gap-3 mt-6">
                          <button type="button" onClick={handleCloseForm} className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={saving}
                            className="rounded-xl bg-[#007BFF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0066CC] disabled:opacity-50 transition-all"
                          >
                            {saving ? 'Saving…' : 'Save changes'}
                          </button>
                        </div>
                      </form>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-2xl border border-[#007BFF]/20 bg-white shadow-md overflow-hidden"
        >
          <div className="bg-gradient-to-r from-[#007BFF]/5 to-transparent px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Create new package</h2>
            <p className="text-sm text-gray-600 mt-0.5">Define quotas, pricing, and visibility</p>
          </div>
          <form onSubmit={handleSubmit} className="p-6">
            <PackageFormFields form={form} setForm={setForm} labelCls={labelCls} inputCls={inputCls} />
            <div className="flex items-center justify-end gap-3 mt-6">
              <button type="button" onClick={handleCloseForm} className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0066CC] disabled:opacity-50"
              >
                {saving ? 'Creating…' : 'Create package'}
              </button>
            </div>
          </form>
        </motion.div>
      )}
    </div>
  );
}

function PackageFormFields({ form, setForm, labelCls, inputCls }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase">Basic</h3>
        <div>
          <label className={labelCls}>Name *</label>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => {
              const name = e.target.value;
              const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
              setForm((prev) => ({ ...prev, name, slug }));
            }}
            placeholder="e.g. Growth Pro"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Slug</label>
          <input
            type="text"
            value={form.slug}
            readOnly
            placeholder="growth-pro"
            className={`${inputCls} bg-gray-50 cursor-not-allowed`}
            title="Auto-generated from name"
          />
        </div>
        <div>
          <label className={labelCls}>Display name (optional)</label>
          <input
            type="text"
            value={form.displayName}
            onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
            placeholder="Friendly name for UI"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Target audience</label>
          <input
            type="text"
            value={form.target}
            onChange={(e) => setForm((prev) => ({ ...prev, target: e.target.value }))}
            placeholder="e.g. Mid-size teams, 5–10 hires/mo"
            className={inputCls}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase">Pricing</h3>
        <div>
          <label className={labelCls}>Monthly price (INR)</label>
          <input
            type="number"
            min="0"
            value={form.monthlyPrice}
            onChange={(e) => setForm((prev) => ({ ...prev, monthlyPrice: e.target.value }))}
            placeholder="24999"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Yearly discount (e.g. 0.05 = 5%)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={form.yearlyDiscount}
            onChange={(e) => setForm((prev) => ({ ...prev, yearlyDiscount: e.target.value }))}
            placeholder="0.05"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Base plan (inherit defaults)</label>
          <select
            value={form.basePlan}
            onChange={(e) => setForm((prev) => ({ ...prev, basePlan: e.target.value }))}
            className={inputCls}
          >
            {BASE_PLANS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-4 md:col-span-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase">Quotas</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(QUOTA_LABELS).map(([key, label]) => (
            <div key={key}>
              <label className={labelCls}>{label}</label>
              <input
                type="number"
                min="0"
                value={form[key]}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder="—"
                className={inputCls}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4 md:col-span-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase">On-demand rates (INR per unit)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.entries(RATE_LABELS).map(([key, label]) => (
            <div key={key}>
              <label className={labelCls}>{label}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form[key]}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder="—"
                className={inputCls}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4 md:col-span-2">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="isHidden"
            checked={form.isHidden}
            onChange={(e) => setForm((prev) => ({ ...prev, isHidden: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
          />
          <label htmlFor="isHidden" className="text-sm font-medium text-gray-700">
            Hidden package (only visible to organisations you explicitly assign)
          </label>
        </div>
        {form.isHidden && (
          <div>
            <label className={labelCls}>Visible to org IDs (one per line or comma-separated)</label>
            <textarea
              value={form.visibleToOrgIdsStr}
              onChange={(e) => setForm((prev) => ({ ...prev, visibleToOrgIdsStr: e.target.value }))}
              placeholder="Paste organisation ObjectIds from Internal Dashboard"
              rows={3}
              className={inputCls}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default InternalPackages;
